/**
 * Ответы API для просмотрщика (сервер: routes/projects/glossary.ts): индекс ударений проекта и кандидаты в формы слова.
 * Проверяются схемой, как glossaryModel.
 */
import { z } from 'zod';
import { formOriginSchema, formProvenanceShape, stressSourceSchema } from './stressProvenanceModel';

const indexForm = z.object({
  form_id: z.string(),
  form: z.string(),
  form_key: z.string(),
  /** У формы одно ударение или пока ни одного («?») */
  variants: z.array(z.string()).max(1),
  status: z.enum(['approved', 'rejected']),
  /** Откуда ударение и склонение — значок в карточке слова; нет — сервер старый */
  origin: formOriginSchema.optional(),
  ...formProvenanceShape,
});

/** Запись индекса: без чисел — для знаков на странице и карточки слова (откуда ударение — значок у слова) */
export const stressIndexEntrySchema = z.object({
  entry_id: z.string(),
  scope: z.enum(['book', 'shared']),
  kind: z.enum(['stress', 'pronunciation']),
  word: z.string(),
  word_key: z.string(),
  stressed: z.string().nullable(),
  pronunciation: z.string().nullable(),
  updated_at: z.string(),
  stress_source: stressSourceSchema,
  forms: z.array(indexForm),
});
export const stressIndexResponseSchema = z.object({ entries: z.array(stressIndexEntrySchema) });

/**
 * Семья слова для меню (сервер: services/glossary/wordFamily.ts): head — начальная форма с числом в книге (бывает 0),
 * forms — все формы слова в книге, начальная тоже, если она там есть. dictionary — статьи словаря начальной формы
 * (@stress/dictionaryStress) с вариантами только этих форм; словарь слова не знает или ответ старый — null.
 */
export const wordFormsSchema = z.object({
  head: z.object({ word_key: z.string(), display: z.string(), count: z.number().int().min(0) }),
  forms: z.array(z.object({ form: z.string(), form_key: z.string(), count: z.number().int().min(0) })),
  dictionary: z
    .object({
      source: z.string(),
      dump_date: z.string(),
      articles: z.array(z.object({ heads: z.array(z.string()), forms: z.record(z.string(), z.array(z.string())) })),
    })
    .nullable()
    .default(null),
});

/** Пример слова: лучшее предложение книги и его страница (сервер: models/stressEntries.ts loadFirstContexts) */
export const wordContextSchema = z.object({ sentence: z.string(), page: z.number().int().min(0) });
/** Примеры для карточки слова по ключам (сервер: GET glossary/contexts); ключа нет в книге — нет и в ответе */
export const wordContextsSchema = z.object({ contexts: z.record(z.string(), wordContextSchema) });

export type StressIndexEntry = z.infer<typeof stressIndexEntrySchema>;
export type StressIndexForm = z.infer<typeof indexForm>;
export type WordForms = z.infer<typeof wordFormsSchema>;
export type WordContext = z.infer<typeof wordContextSchema>;
/** Ответ с ETag: notModified — у клиента уже эта версия */
export type StressIndexFetch = { notModified: true } | { notModified: false; etag: string | null; entries: StressIndexEntry[] };
/** Решение меню слова по форме (сервер: glossaryContract formDecision) */
export type FormDecision = { form_key: string; variants: string[]; status: 'approved' | 'rejected' };
